import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert, Divider, Tooltip, Tabs, Tab,
  FormControlLabel, Switch
} from '@mui/material';
import { Add, Edit, Delete, Save as SaveIcon, Info, CheckCircle, School } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { passingCriteriaAPI, promotionRulesAPI, levelsAPI, settingsAPI } from '../services/api';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

const AGGREGATE_METHODS = [
  { value: 'all_subjects',     label: 'Average of All Subjects',                              showBestN: false, showMinCores: false,
    description: 'Aggregate is the simple average across every subject the student takes. All subjects count equally.' },
  { value: 'best_n',           label: 'Best N Subjects Only',                                 showBestN: true,  showMinCores: false,
    description: 'Only the student\'s N highest-scoring subjects are averaged. Lower-scoring subjects are excluded. Example: Best 6 subjects.' },
  { value: 'core_plus_best_n', label: 'All Core Subjects + Best N Electives',                 showBestN: true,  showMinCores: false,
    description: 'All compulsory/core subjects are always included. N best-scoring elective subjects are added. Example: 5 cores + best 3 electives.' },
  { value: 'best_n_inc_core',  label: 'Best N Subjects (Must Include Minimum Cores)',          showBestN: true,  showMinCores: true,
    description: 'Best N subjects are selected, but at least M of them must be core subjects. Example: Best 6 including at least 2 cores.' },
];

const PASS_DECISIONS = [
  { value: 'average_threshold',    label: 'Aggregate Above Minimum %',                    showMinSubjects: false, showMinCores: false,
    description: 'Student is promoted if their calculated aggregate percentage meets or exceeds the threshold. Example: aggregate ≥ 50%.' },
  { value: 'count_passes',         label: 'Pass Minimum Number of Subjects',               showMinSubjects: true,  showMinCores: false,
    description: 'Student must individually pass at least N subjects (score ≥ subject pass mark). Example: must pass at least 6 subjects.' },
  { value: 'count_passes_inc_core','label': 'Pass N Subjects Including Minimum Cores',     showMinSubjects: true,  showMinCores: true,
    description: 'Must pass at least N subjects AND at least M of those must be core subjects. Example: pass 6 including at least 3 cores.' },
];

const EMPTY_RULE = {
  name: '', level_id: '', class_id: '', academic_year_id: '', grading_scale_id: '',
  aggregate_method: 'all_subjects', best_n_subjects: 6, min_cores_in_best_n: 2,
  pass_decision: 'average_threshold', min_aggregate_percent: 50,
  min_subjects_passed: 6, min_cores_passed: 3,
  subject_pass_mark: 50, min_attendance_percent: 0, notes: '', is_active: true
};

const DEFAULT_BOUNDARIES = [
  { grade: 'A*', min_percent: 90, max_percent: 100, label: 'Outstanding',   points: 5.0 },
  { grade: 'A',  min_percent: 80, max_percent: 89,  label: 'Distinction',   points: 4.0 },
  { grade: 'B',  min_percent: 70, max_percent: 79,  label: 'Merit',         points: 3.0 },
  { grade: 'C',  min_percent: 60, max_percent: 69,  label: 'Credit',        points: 2.5 },
  { grade: 'D',  min_percent: 50, max_percent: 59,  label: 'Pass',          points: 2.0 },
  { grade: 'E',  min_percent: 40, max_percent: 49,  label: 'Below Average', points: 1.0 },
  { grade: 'F',  min_percent: 0,  max_percent: 39,  label: 'Fail',          points: 0.0 },
];

export default function PassingCriteria() {
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState(0);
  const [scales, setScales] = useState([]);
  const [rules, setRules] = useState([]);
  const [levels, setLevels] = useState([]);
  const [classes, setClasses] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [activeScale, setActiveScale] = useState(null);
  const [boundaries, setBoundaries] = useState(DEFAULT_BOUNDARIES);
  const [scaleSaving, setScaleSaving] = useState(false);
  const [newScaleName, setNewScaleName] = useState('');
  const [ruleDialog, setRuleDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm, setRuleForm] = useState(EMPTY_RULE);
  const [ruleSaving, setRuleSaving] = useState(false);

  const load = async () => {
    try {
      const [scalesRes, rulesRes, levelsRes, classesRes, yearsRes] = await Promise.all([
        passingCriteriaAPI.getGradingScales(), promotionRulesAPI.getAll(),
        levelsAPI.getAll(), settingsAPI.getClasses(), settingsAPI.getAcademicYears(),
      ]);
      if (scalesRes.data.success) {
        setScales(scalesRes.data.data);
        const def = scalesRes.data.data.find(s => s.is_default);
        if (def) { setActiveScale(def); setBoundaries(def.boundaries?.length ? def.boundaries : DEFAULT_BOUNDARIES); }
      }
      if (rulesRes.data.success) setRules(rulesRes.data.data);
      if (levelsRes.data.success) setLevels(levelsRes.data.data);
      if (classesRes.data.success) setClasses(classesRes.data.data);
      if (yearsRes.data.success) setAcademicYears(yearsRes.data.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, []);

  const handleBoundaryChange = (idx, field, value) => {
    setBoundaries(prev => prev.map((b, i) => i === idx ? { ...b, [field]: value } : b));
  };

  const createScale = async () => {
    if (!newScaleName) return enqueueSnackbar('Enter a scale name', { variant: 'warning' });
    try {
      await passingCriteriaAPI.createGradingScale({ name: newScaleName, is_default: scales.length === 0 ? 1 : 0 });
      setNewScaleName(''); enqueueSnackbar('Scale created', { variant: 'success' }); load();
    } catch { enqueueSnackbar('Error creating scale', { variant: 'error' }); }
  };

  const saveBoundaries = async () => {
    if (!activeScale) return;
    setScaleSaving(true);
    try {
      await passingCriteriaAPI.saveGradeBoundaries({ grading_scale_id: activeScale.id, boundaries });
      enqueueSnackbar('Grade boundaries saved', { variant: 'success' }); load();
    } catch { enqueueSnackbar('Error saving', { variant: 'error' }); }
    finally { setScaleSaving(false); }
  };

  const openCreate = () => {
    setEditingRule(null);
    const cur = academicYears.find(y => y.is_current);
    setRuleForm({ ...EMPTY_RULE, academic_year_id: cur?.id || '', grading_scale_id: scales.find(s => s.is_default)?.id || '' });
    setRuleDialog(true);
  };

  const openEdit = (rule) => {
    setEditingRule(rule);
    setRuleForm({
      name: rule.name, level_id: rule.level_id || '', class_id: rule.class_id || '',
      academic_year_id: rule.academic_year_id || '', grading_scale_id: rule.grading_scale_id || '',
      aggregate_method: rule.aggregate_method, best_n_subjects: rule.best_n_subjects || 6,
      min_cores_in_best_n: rule.min_cores_in_best_n || 2, pass_decision: rule.pass_decision,
      min_aggregate_percent: rule.min_aggregate_percent || 50, min_subjects_passed: rule.min_subjects_passed || 6,
      min_cores_passed: rule.min_cores_passed || 3, subject_pass_mark: rule.subject_pass_mark || 50,
      min_attendance_percent: rule.min_attendance_percent || 0, notes: rule.notes || '', is_active: !!rule.is_active
    });
    setRuleDialog(true);
  };

  const saveRule = async () => {
    if (!ruleForm.name) return enqueueSnackbar('Rule name is required', { variant: 'warning' });
    setRuleSaving(true);
    try {
      await promotionRulesAPI.save({ ...ruleForm, id: editingRule?.id });
      enqueueSnackbar(editingRule ? 'Rule updated' : 'Rule created', { variant: 'success' });
      setRuleDialog(false); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
    finally { setRuleSaving(false); }
  };

  const deleteRule = async (id) => {
    if (!window.confirm('Delete this promotion rule?')) return;
    try { await promotionRulesAPI.delete(id); enqueueSnackbar('Deleted', { variant: 'success' }); load(); }
    catch { enqueueSnackbar('Error deleting', { variant: 'error' }); }
  };

  const aggMethod = AGGREGATE_METHODS.find(m => m.value === ruleForm.aggregate_method);
  const passDecision = PASS_DECISIONS.find(d => d.value === ruleForm.pass_decision);
  const scopeLabel = (rule) => rule.class_name ? `Class: ${rule.class_name}${rule.class_stream ? ' '+rule.class_stream:''}` : rule.level_name ? `Level: ${rule.level_name}` : 'All classes';

  return (
    <Box>
      <Typography variant="h5" fontWeight={800} gutterBottom>Grading & Promotion Rules</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Promotion Rules" />
        <Tab label="Grading Scales & Boundaries" />
      </Tabs>

      {/* PROMOTION RULES */}
      <TabPanel value={tab} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Promotion Rules</Typography>
            <Typography variant="body2" color="text.secondary">
              Define how aggregates are calculated and what constitutes a pass. Rules can target a whole level or a specific class.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}
            sx={{ bgcolor: '#0f2b4a', '&:hover': { bgcolor: '#1a3a5c' } }}>Add Rule</Button>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Priority:</strong> Class-specific rule → Level rule → Generic rule (no level or class set).
        </Alert>

        {rules.length === 0 ? (
          <Card sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
            <School sx={{ fontSize: 64, opacity: 0.15, display: 'block', mx: 'auto', mb: 2 }} />
            <Typography>No promotion rules yet. Click "Add Rule" to get started.</Typography>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {rules.map(rule => {
              const am = AGGREGATE_METHODS.find(m => m.value === rule.aggregate_method);
              const pd = PASS_DECISIONS.find(d => d.value === rule.pass_decision);
              return (
                <Grid item xs={12} md={6} key={rule.id}>
                  <Card sx={{ borderLeft: `5px solid ${rule.is_active ? '#0f2b4a' : '#ccc'}`, height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography fontWeight={800} fontSize={15}>{rule.name}</Typography>
                          <Box sx={{ display: 'flex', gap: 0.8, mt: 0.5, flexWrap: 'wrap' }}>
                            <Chip label={scopeLabel(rule)} size="small" color="primary" />
                            {rule.year_label && <Chip label={`${rule.year_label} ${rule.term||''}`} size="small" variant="outlined" />}
                            <Chip label={rule.is_active ? 'Active' : 'Inactive'} size="small" color={rule.is_active ? 'success' : 'default'} />
                          </Box>
                        </Box>
                        <Box>
                          <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(rule)}><Edit fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => deleteRule(rule.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                        </Box>
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>Aggregate Method</Typography>
                        <Typography fontSize={13} fontWeight={600}>{am?.label}</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.4, flexWrap: 'wrap' }}>
                          {rule.best_n_subjects && <Chip label={`N = ${rule.best_n_subjects}`} size="small" sx={{ fontSize: 9 }} />}
                          {rule.min_cores_in_best_n && <Chip label={`Min ${rule.min_cores_in_best_n} cores`} size="small" sx={{ fontSize: 9 }} />}
                        </Box>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>Pass Decision</Typography>
                        <Typography fontSize={13} fontWeight={600}>{pd?.label}</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.4, flexWrap: 'wrap' }}>
                          {rule.pass_decision === 'average_threshold' && <Chip label={`≥ ${rule.min_aggregate_percent}%`} size="small" color="warning" sx={{ fontSize: 9 }} />}
                          {rule.min_subjects_passed && <Chip label={`Pass ≥ ${rule.min_subjects_passed} subj.`} size="small" color="warning" sx={{ fontSize: 9 }} />}
                          {rule.min_cores_passed && <Chip label={`Inc. ≥ ${rule.min_cores_passed} cores`} size="small" color="error" sx={{ fontSize: 9 }} />}
                          <Chip label={`Subj. pass = ${rule.subject_pass_mark}%`} size="small" sx={{ fontSize: 9 }} />
                        </Box>
                      </Box>
                      {rule.notes && <Typography variant="caption" color="text.secondary" fontStyle="italic" display="block" sx={{ mt: 0.8 }}>{rule.notes}</Typography>}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </TabPanel>

      {/* GRADING SCALES */}
      <TabPanel value={tab} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>Grading Scales</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                  {scales.map(s => (
                    <Box key={s.id}
                      onClick={() => { setActiveScale(s); setBoundaries(s.boundaries?.length ? s.boundaries : DEFAULT_BOUNDARIES); }}
                      sx={{ p: 1.5, borderRadius: 2, cursor: 'pointer', border: '1.5px solid',
                        borderColor: activeScale?.id === s.id ? '#0f2b4a' : 'divider',
                        bgcolor: activeScale?.id === s.id ? 'rgba(15,43,74,0.06)' : 'transparent' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography fontWeight={700} fontSize={13}>{s.name}</Typography>
                        {s.is_default && <Chip label="Default" size="small" color="primary" sx={{ fontSize: 9 }} />}
                      </Box>
                      <Typography variant="caption" color="text.secondary">{s.boundaries?.length || 0} grades</Typography>
                    </Box>
                  ))}
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" gutterBottom>New Scale</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField size="small" placeholder="Scale name" value={newScaleName}
                    onChange={e => setNewScaleName(e.target.value)} sx={{ flex: 1 }} />
                  <Button variant="contained" size="small" onClick={createScale}
                    sx={{ bgcolor: '#0f2b4a', '&:hover': { bgcolor: '#1a3a5c' } }}>Add</Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight={700}>
                    {activeScale ? `Boundaries — ${activeScale.name}` : 'Select a grading scale'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" startIcon={<Add />} onClick={() => setBoundaries(prev => [...prev, { grade: '', min_percent: 0, max_percent: 0, label: '', points: 0 }])}>
                      Add Grade
                    </Button>
                    <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={saveBoundaries}
                      disabled={scaleSaving || !activeScale}
                      sx={{ bgcolor: '#0f2b4a', '&:hover': { bgcolor: '#1a3a5c' } }}>
                      {scaleSaving ? <CircularProgress size={16} /> : 'Save'}
                    </Button>
                  </Box>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#0f2b4a' }}>
                        {['Grade','Min %','Max %','Label','Points (GPA)',''].map(h => (
                          <TableCell key={h} sx={{ color: 'rgba(255,255,255,.8)', fontWeight: 700, fontSize: 10 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {boundaries.map((b, idx) => (
                        <TableRow key={idx} hover sx={{ bgcolor: idx % 2 === 0 ? 'white' : '#f7f8fc' }}>
                          <TableCell><TextField size="small" value={b.grade} sx={{ width: 52 }} onChange={e => handleBoundaryChange(idx, 'grade', e.target.value)} /></TableCell>
                          <TableCell><TextField size="small" type="number" value={b.min_percent} sx={{ width: 64 }} onChange={e => handleBoundaryChange(idx, 'min_percent', parseFloat(e.target.value))} /></TableCell>
                          <TableCell><TextField size="small" type="number" value={b.max_percent} sx={{ width: 64 }} onChange={e => handleBoundaryChange(idx, 'max_percent', parseFloat(e.target.value))} /></TableCell>
                          <TableCell><TextField size="small" value={b.label} sx={{ width: 130 }} placeholder="e.g. Distinction" onChange={e => handleBoundaryChange(idx, 'label', e.target.value)} /></TableCell>
                          <TableCell><TextField size="small" type="number" value={b.points} sx={{ width: 60 }} inputProps={{ step: 0.5 }} onChange={e => handleBoundaryChange(idx, 'points', parseFloat(e.target.value))} /></TableCell>
                          <TableCell><IconButton size="small" color="error" onClick={() => setBoundaries(prev => prev.filter((_, i) => i !== idx))}><Delete fontSize="small" /></IconButton></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* RULE DIALOG */}
      <Dialog open={ruleDialog} onClose={() => setRuleDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#0f2b4a', color: 'white' }}>
          {editingRule ? 'Edit Promotion Rule' : 'Create Promotion Rule'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField label="Rule Name *" value={ruleForm.name}
                onChange={e => setRuleForm({ ...ruleForm, name: e.target.value })}
                placeholder="e.g. Junior Standard, Senior O-Level" />
            </Grid>

            {/* Scope */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Scope</Typography>
              <Alert severity="info" sx={{ py: 0.5, mb: 1 }}>
                Leave blank for a generic rule. Set Level to cover all classes at that level. Set a specific Class to override the level rule.
              </Alert>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select label="Level" value={ruleForm.level_id} onChange={e => setRuleForm({ ...ruleForm, level_id: e.target.value })}>
                <MenuItem value="">All levels</MenuItem>
                {levels.map(l => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select label="Specific Class (optional override)" value={ruleForm.class_id} onChange={e => setRuleForm({ ...ruleForm, class_id: e.target.value })}>
                <MenuItem value="">No specific class</MenuItem>
                {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}{c.stream ? ` (${c.stream})` : ''}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select label="Academic Year" value={ruleForm.academic_year_id} onChange={e => setRuleForm({ ...ruleForm, academic_year_id: e.target.value })}>
                <MenuItem value="">All years</MenuItem>
                {(academicYears.some(y => y.is_current) ? academicYears.filter(y => y.is_current) : academicYears).map(y => (
                <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term} (Current)</MenuItem>
              ))}
              </TextField>
            </Grid>

            <Grid item xs={12}><Divider><Typography variant="caption" fontWeight={700} color="text.secondary">STEP 1 — HOW IS THE AGGREGATE CALCULATED?</Typography></Divider></Grid>

            <Grid item xs={12}>
              <TextField select label="Aggregate Calculation Method *" value={ruleForm.aggregate_method}
                onChange={e => setRuleForm({ ...ruleForm, aggregate_method: e.target.value })}>
                {AGGREGATE_METHODS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </TextField>
              {aggMethod && <Alert severity="info" icon={<Info />} sx={{ mt: 1, py: 0.5 }}><Typography fontSize={12}>{aggMethod.description}</Typography></Alert>}
            </Grid>
            {aggMethod?.showBestN && (
              <Grid item xs={12} sm={6}>
                <TextField label={`N — Number of subjects in aggregate`} type="number"
                  value={ruleForm.best_n_subjects}
                  onChange={e => setRuleForm({ ...ruleForm, best_n_subjects: parseInt(e.target.value) })}
                  inputProps={{ min: 1, max: 30 }}
                  helperText="How many subjects are included in the average" />
              </Grid>
            )}
            {aggMethod?.showMinCores && (
              <Grid item xs={12} sm={6}>
                <TextField label="Minimum core subjects that must be in N" type="number"
                  value={ruleForm.min_cores_in_best_n}
                  onChange={e => setRuleForm({ ...ruleForm, min_cores_in_best_n: parseInt(e.target.value) })}
                  inputProps={{ min: 0 }}
                  helperText="At least this many of the N selected must be core subjects" />
              </Grid>
            )}

            <Grid item xs={12}><Divider><Typography variant="caption" fontWeight={700} color="text.secondary">STEP 2 — WHAT COUNTS AS A PASS?</Typography></Divider></Grid>

            <Grid item xs={12}>
              <TextField select label="Pass Decision Method *" value={ruleForm.pass_decision}
                onChange={e => setRuleForm({ ...ruleForm, pass_decision: e.target.value })}>
                {PASS_DECISIONS.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
              </TextField>
              {passDecision && <Alert severity="success" icon={<CheckCircle />} sx={{ mt: 1, py: 0.5 }}><Typography fontSize={12}>{passDecision.description}</Typography></Alert>}
            </Grid>
            {ruleForm.pass_decision === 'average_threshold' && (
              <Grid item xs={12} sm={6}>
                <TextField label="Minimum Aggregate %" type="number"
                  value={ruleForm.min_aggregate_percent}
                  onChange={e => setRuleForm({ ...ruleForm, min_aggregate_percent: parseFloat(e.target.value) })}
                  inputProps={{ min: 0, max: 100, step: 0.5 }}
                  helperText="Student promotes if aggregate ≥ this" />
              </Grid>
            )}
            {passDecision?.showMinSubjects && (
              <Grid item xs={12} sm={6}>
                <TextField label="Minimum subjects student must individually pass" type="number"
                  value={ruleForm.min_subjects_passed}
                  onChange={e => setRuleForm({ ...ruleForm, min_subjects_passed: parseInt(e.target.value) })}
                  inputProps={{ min: 1 }}
                  helperText="Each subject must be ≥ subject pass mark" />
              </Grid>
            )}
            {passDecision?.showMinCores && (
              <Grid item xs={12} sm={6}>
                <TextField label="Minimum core subjects that must be passed" type="number"
                  value={ruleForm.min_cores_passed}
                  onChange={e => setRuleForm({ ...ruleForm, min_cores_passed: parseInt(e.target.value) })}
                  inputProps={{ min: 0 }}
                  helperText="Of the N passed subjects, at least this many must be core" />
              </Grid>
            )}

            <Grid item xs={12}><Divider><Typography variant="caption" fontWeight={700} color="text.secondary">STEP 3 — SUPPORTING SETTINGS</Typography></Divider></Grid>

            <Grid item xs={12} sm={4}>
              <TextField label="Subject Pass Mark (%)" type="number"
                value={ruleForm.subject_pass_mark}
                onChange={e => setRuleForm({ ...ruleForm, subject_pass_mark: parseFloat(e.target.value) })}
                inputProps={{ min: 0, max: 100 }}
                helperText="Score to 'pass' a single subject" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select label="Grading Scale" value={ruleForm.grading_scale_id}
                onChange={e => setRuleForm({ ...ruleForm, grading_scale_id: e.target.value })}>
                <MenuItem value="">Use default</MenuItem>
                {scales.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Min Attendance % (0 = not enforced)" type="number"
                value={ruleForm.min_attendance_percent}
                onChange={e => setRuleForm({ ...ruleForm, min_attendance_percent: parseFloat(e.target.value) })}
                inputProps={{ min: 0, max: 100 }}
                helperText="0 means attendance is not checked" />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Notes / Description" value={ruleForm.notes}
                onChange={e => setRuleForm({ ...ruleForm, notes: e.target.value })}
                multiline rows={2} placeholder="e.g. Based on LGCSE Junior regulations 2024" />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel control={
                <Switch checked={!!ruleForm.is_active} onChange={e => setRuleForm({ ...ruleForm, is_active: e.target.checked })} />
              } label="Rule is active" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRuleDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveRule} disabled={ruleSaving}
            sx={{ bgcolor: '#0f2b4a', '&:hover': { bgcolor: '#1a3a5c' } }}>
            {ruleSaving ? <CircularProgress size={20} /> : editingRule ? 'Update Rule' : 'Create Rule'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
